// Upload a binary file (PDF/DOCX/XLSX) for a workflow step output and wait
// for server-side text extraction.
//
// Flow:
//   1. Insert a builder_attachments row with workflow_conversation_id set
//      and extraction_status = 'pending'.
//   2. Upload the file binary to the `builder-attachments` storage bucket
//      at the path expected by the existing storage RLS:
//        {userId}/{workflowId}/{timestamp}_{filename}
//   3. Invoke `extract-file-text` with attachment_id.
//   4. Poll the row until extraction_status flips to 'completed' or 'failed'.
//   5. Return the extracted text (or throw).

import { supabase } from '@/integrations/supabase/client';

const MAX_BINARY_BYTES = 20 * 1024 * 1024; // 20MB
const POLL_INTERVAL_MS = 1200;
const POLL_TIMEOUT_MS = 60_000;

const BINARY_MIME_HINTS: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export interface UploadAndExtractResult {
  text: string;
  filename: string;
  mimeType: string;
  bytes: number;
  attachmentId: string;
}

export class WorkflowAttachmentError extends Error {
  constructor(public readonly stage: 'auth' | 'upload' | 'insert' | 'invoke' | 'poll' | 'timeout' | 'failed', message: string) {
    super(message);
    this.name = 'WorkflowAttachmentError';
  }
}

function guessMime(file: File): string {
  if (file.type) return file.type;
  const lower = file.name.toLowerCase();
  for (const ext of Object.keys(BINARY_MIME_HINTS)) {
    if (lower.endsWith(ext)) return BINARY_MIME_HINTS[ext];
  }
  return 'application/octet-stream';
}

function sanitizeFilename(name: string): string {
  // Strip any characters that could confuse storage paths.
  return name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 120) || 'file';
}

export async function uploadWorkflowFileAndExtract(
  file: File,
  workflowConversationId: string,
): Promise<UploadAndExtractResult> {
  if (file.size > MAX_BINARY_BYTES) {
    throw new WorkflowAttachmentError('upload', `File too large (max ${MAX_BINARY_BYTES / 1024 / 1024}MB).`);
  }

  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) {
    throw new WorkflowAttachmentError('auth', 'You must be signed in to upload files.');
  }
  const userId = authData.user.id;

  const safeName = sanitizeFilename(file.name);
  const mime = guessMime(file);
  const storagePath = `${userId}/${workflowConversationId}/${Date.now()}_${safeName}`;

  // Upload to storage first — if the bucket rejects, we don't end up with a
  // dangling DB row.
  const uploadRes = await supabase.storage
    .from('builder-attachments')
    .upload(storagePath, file, {
      contentType: mime,
      cacheControl: '3600',
      upsert: false,
    });
  if (uploadRes.error) {
    throw new WorkflowAttachmentError('upload', uploadRes.error.message);
  }

  // Insert the metadata row.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const insertRes: any = await (supabase as any)
    .from('builder_attachments')
    .insert({
      user_id: userId,
      workflow_conversation_id: workflowConversationId,
      conversation_id: null,
      storage_path: storagePath,
      file_name: file.name,
      file_size: file.size,
      mime_type: mime,
      extraction_status: 'pending',
    })
    .select('id')
    .single();
  if (insertRes.error || !insertRes.data) {
    // Best-effort cleanup of the stored file so we don't orphan blobs.
    await supabase.storage.from('builder-attachments').remove([storagePath]).catch(() => undefined);
    throw new WorkflowAttachmentError('insert', insertRes.error?.message ?? 'Could not record the attachment.');
  }
  const attachmentId: string = insertRes.data.id;

  // Kick off extraction.
  const invokeRes = await supabase.functions.invoke('extract-file-text', {
    body: { attachment_id: attachmentId },
  });
  if (invokeRes.error) {
    throw new WorkflowAttachmentError('invoke', invokeRes.error.message ?? 'Extraction call failed.');
  }

  // Poll until completed or failed.
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row: any = await (supabase as any)
      .from('builder_attachments')
      .select('extraction_status, extracted_text, extraction_error')
      .eq('id', attachmentId)
      .single();
    if (row.error) {
      throw new WorkflowAttachmentError('poll', row.error.message);
    }
    const status = row.data?.extraction_status as string | undefined;
    if (status === 'completed') {
      const text = (row.data?.extracted_text as string | null)?.trim();
      if (!text) {
        throw new WorkflowAttachmentError('failed', 'Extraction completed but produced no text.');
      }
      return {
        text,
        filename: file.name,
        mimeType: mime,
        bytes: file.size,
        attachmentId,
      };
    }
    if (status === 'failed') {
      throw new WorkflowAttachmentError('failed', row.data?.extraction_error ?? 'Extraction failed.');
    }
  }
  throw new WorkflowAttachmentError('timeout', 'Extraction timed out. Try a smaller file or paste the text.');
}
